import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import HttpService from "src/services/httpService";
import RouteService from "src/services/routeService";
import useDebounce from "src/ui/hooks/useDebounce";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";
import { getOrganizationUrlValidationError } from "src/ui/utils/validityChecker";

import { checkGuidAvailability } from "@admin/api/workspace";
import { unpackError } from "@dashboard/devices/api/helpers";
import { useTheme } from "@mui/material";
import { UncontrolledTextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

interface Props {
    workspace: API.Workspace;
    open: boolean;
    onClose: () => void;
    onSuccess: (updatedWorkspace: API.Workspace) => void;
}

const EditWorkspaceModal = ({ workspace, open, onClose, onSuccess }: Props) => {
    const [guidError, setGuidError] = React.useState<string | undefined>();
    const [nameError, setNameError] = React.useState<string | undefined>();
    const [isValidating, setIsValidating] = React.useState(false);
    const [currentGuid, setCurrentGuid] = React.useState(workspace.guid);
    const [currentName, setCurrentName] = React.useState(workspace.name);
    const [currentOrganization, setCurrentOrganization] = React.useState(workspace.organizationName || "");
    const [currentOrganizationUrl, setCurrentOrganizationUrl] = React.useState(workspace.organizationUrl ?? "");
    const [organizationUrlError, setOrganizationUrlError] = React.useState<string | undefined>();

    const theme = useTheme();
    const { t } = useTranslation(["modals", "pages", "common"]);

    const updateWorkspace = async (
        workspaceData: API.Workspace,
        newGuid: string,
        newName: string,
        newOrganization: string,
        newOrganizationUrl: string,
    ) => {
        try {
            const endpoint = await RouteService.getWorkspacesRoute();
            const payload = {
                name: newName,
                organizationName: newOrganization,
                organizationUrl: newOrganizationUrl.trim(),
                guid: newGuid,
                organizationVatId: workspaceData.organizationVatId || "",
                defaultGroupId: workspaceData.defaultGroupId,
            };
            const { data } = await HttpService.put<API.Workspace>(`${endpoint}${workspaceData.id}`, payload);
            return data;
        } catch (error) {
            throw unpackError(error);
        }
    };

    const validateGuid = useCallback(
        async (guid: string) => {
            if (!guid.trim() || guid === workspace.guid) {
                setGuidError(undefined);
                setIsValidating(false);
                return;
            }

            setIsValidating(true);
            try {
                const result = await checkGuidAvailability(guid);

                if (result.available) {
                    setGuidError(undefined);
                } else {
                    setGuidError(t("modals:adminEditWorkspace.validation.guidTaken"));
                }
            } catch (_error) {
                setGuidError(t("modals:adminEditWorkspace.validation.guidCheckError"));
            } finally {
                setIsValidating(false);
            }
        },
        [workspace.guid, t],
    );

    const debouncedValidateGuid = useDebounce(validateGuid, 1000);

    const handleGuidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setCurrentGuid(newValue);
        void debouncedValidateGuid(newValue);

        if (guidError && newValue !== currentGuid) {
            setGuidError(undefined);
        }
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setCurrentName(newValue);

        if (!newValue.trim()) {
            setNameError(t("common:validation.custom.nameRequired"));
        } else if (nameError) {
            setNameError(undefined);
        }
    };

    const handleOrganizationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setCurrentOrganization(newValue);
    };

    const handleOrganizationUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setCurrentOrganizationUrl(newValue);

        const validationError = getOrganizationUrlValidationError(newValue.trim());
        setOrganizationUrlError(validationError ? t(`common:validation.url.${validationError}`) : undefined);
    };

    const onSubmit = async () => {
        if (guidError || nameError || organizationUrlError || !currentGuid.trim() || !currentName.trim()) {
            return;
        }

        try {
            const updatedWorkspace = await updateWorkspace(
                workspace,
                currentGuid,
                currentName,
                currentOrganization,
                currentOrganizationUrl,
            );
            showSuccessToast(t("modals:adminEditWorkspace.toast.workspaceUpdatedSuccess"));
            onSuccess(updatedWorkspace);
        } catch (error: unknown) {
            const err = error as Error;
            showErrorToast(err.message || t("modals:adminEditWorkspace.toast.workspaceUpdatedError"));
        }
    };

    const hasError = !!guidError || !!nameError || !!organizationUrlError;
    const isPristine =
        currentGuid === workspace.guid &&
        currentName === workspace.name &&
        currentOrganization === (workspace.organizationName || "") &&
        currentOrganizationUrl === (workspace.organizationUrl ?? "");
    const isDisabled = isPristine || isValidating || hasError || !currentGuid.trim() || !currentName.trim();

    const getErrorMessage = () => {
        if (guidError) {
            return guidError;
        }
        if (!currentGuid.trim()) {
            return t("modals:adminEditWorkspace.validation.guidRequired");
        }
        return undefined;
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={t("modals:adminEditWorkspace.title")}
            endButton={
                <PrimaryButton onClick={onSubmit} disabled={isDisabled} loading={isValidating}>
                    {t("common:button.save")}
                </PrimaryButton>
            }
        >
            <UncontrolledTextField
                label={t("common:label.name")}
                value={currentName}
                onChange={handleNameChange}
                required
                errorMessage={nameError}
                style={{ marginBottom: theme.spacing(3) }}
            />
            <UncontrolledTextField
                label={t("common:label.guid")}
                value={currentGuid}
                onChange={handleGuidChange}
                required
                errorMessage={getErrorMessage()}
                style={{ marginBottom: theme.spacing(3) }}
            />
            <UncontrolledTextField
                label={t("common:label.organization")}
                value={currentOrganization}
                onChange={handleOrganizationChange}
                style={{ marginBottom: theme.spacing(3) }}
            />
            <UncontrolledTextField
                label={t("pages:workspacePage.input.organizationUrl.caption")}
                placeholder={t("pages:workspacePage.input.organizationUrl.placeholder")}
                value={currentOrganizationUrl}
                onChange={handleOrganizationUrlChange}
                errorMessage={organizationUrlError}
            />
        </Modal>
    );
};

export default EditWorkspaceModal;
